import { t } from '@lingui/core/macro';
import { z } from 'zod';

import { type AIVisibilityCompetitor } from '@/api/aiVisibility';
import { useTypedAppFormContext } from '@/components/Form';

export const MIN_PRODUCT_AREAS = 2;
export const MAX_PRODUCT_AREAS = 5;
export const MAX_COMPETITORS = 5;

export const AI_VISIBILITY_SETTINGS_DEFAULT_VALUES = {
  brandProductAreas: [] as string[],
  competitors: [] as AIVisibilityCompetitor[],
  newProductArea: '',
  competitorDomain: '',
  showCompetitors: false,
};

export type AIVisibilitySettingsFormValues = typeof AI_VISIBILITY_SETTINGS_DEFAULT_VALUES;

const competitorSchema = z.object({
  name: z.string(),
  domain: z.string(),
  comparedProductAreas: z.array(z.string()),
});

export const getAIVisibilitySettingsFormSchema = () =>
  z
    .object({
      brandProductAreas: z.array(z.string()).max(MAX_PRODUCT_AREAS),
      competitors: z.array(competitorSchema),
      newProductArea: z.string(),
      competitorDomain: z.string(),
      showCompetitors: z.boolean(),
    })
    .superRefine((value, ctx) => {
      if (!value.showCompetitors) {
        return;
      }

      if (value.brandProductAreas.length < MIN_PRODUCT_AREAS) {
        ctx.addIssue({
          code: 'custom',
          path: ['brandProductAreas'],
          message: t`Add at least two product areas.`,
        });
      }

      value.competitors.forEach((competitor, index) => {
        if (value.brandProductAreas.length === 0) {
          return;
        }

        const overlappingCount = competitor.comparedProductAreas.filter((area) =>
          value.brandProductAreas.includes(area),
        ).length;

        if (overlappingCount < MIN_PRODUCT_AREAS) {
          ctx.addIssue({
            code: 'custom',
            path: ['competitors', index, 'comparedProductAreas'],
            message: t`Add at least two overlapping product areas.`,
          });
        }
      });
    });

export const formValuesFromSavedSettings = (
  settings: Pick<AIVisibilitySettingsFormValues, 'brandProductAreas' | 'competitors'>,
): AIVisibilitySettingsFormValues => ({
  ...AI_VISIBILITY_SETTINGS_DEFAULT_VALUES,
  brandProductAreas: settings.brandProductAreas,
  competitors: settings.competitors,
  showCompetitors: settings.brandProductAreas.length > 0 || settings.competitors.length > 0,
});

export const useAIVisibilitySettingsForm = () =>
  useTypedAppFormContext({ defaultValues: AI_VISIBILITY_SETTINGS_DEFAULT_VALUES });
